import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Stack,
    Typography
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Login from '../login/Login';
import { Media } from '../../components/media';
import { useSession } from '../../contexts/session-context';
import Api from '../../util/Api';
import { UserType } from '../../types/api';
import { mcpConnectCopy as copy } from './mcp-connect-copy';
import type { ApiTokenCreateBody, ApiTokenCreated } from '../business/api-webhooks/types';

/**
 * The consent screen for connecting an AI assistant over MCP (`Cloudflare/mcp`,
 * `docs/connect-handoff.md`; MCP-SERVER-PLAN.md §3.1 Option B).
 *
 * The MCP server has no login and never sees a password. It parks the OAuth request, sends the
 * customer here, and waits: this page signs them in, shows them what is being connected, mints an
 * API token on approval, posts it back and navigates on. That is the whole reason the approval
 * lives here — a consent screen on a host the customer does not recognise is the shape of a
 * phishing page.
 *
 * ★ THE SCREEN IS THE CONTROL. The connection runs on the signed-in user's own permission context
 * (by design — a user's access is already scoped), so there is no role picker. Where the deployment
 * offers scopes (S11) the customer also chooses which areas the assistant may work in; that narrows
 * what it is OFFERED, not what the user is permitted, and nothing narrows it later. The words are in `mcp-connect-copy.ts`, kept separate because they are the product
 * here, not decoration.
 */

/** Herb, 2026-09-21. The MCP worker enforces the same list; this is so the refusal reads well. */
const CONNECTABLE = [UserType.Admin, UserType.Owner, UserType.TeamMember];

/**
 * The MCP deployments this page will hand a token to.
 *
 * ★ AN ALLOWLIST, BECAUSE THE ORIGIN ARRIVES IN THE URL. The worker puts its own origin on the
 * redirect so that one app build serves both deployments — test01's page must post to
 * `mcp-test.hd.pics` and production's to `mcp.hd.pics`, and a value compiled into the bundle cannot
 * be both. But a URL in a query string is request data wherever it came from: without this list,
 * `/ux/connect?handoff=…&mcp=https://evil.example` would POST a freshly minted API token to
 * whoever asked. Add a deployment here deliberately; never take the parameter on trust.
 */
const MCP_ORIGINS = ['https://mcp.hd.pics', 'https://mcp-test.hd.pics'];

function mcpOrigin(params: URLSearchParams): string | undefined {
    const claimed = params.get('mcp');
    return claimed && MCP_ORIGINS.includes(claimed) ? claimed : undefined;
}

/**
 * One area the customer can tick, as the MCP deployment describes it (`GET /oauth/scopes`).
 *
 * ★ THE LIST COMES FROM THE DEPLOYMENT, NEVER FROM THIS BUNDLE (MCP-SERVER-PLAN.md §2.3b, S11).
 * One app build serves both deployments, and until the cutover they serve different tool tables
 * with different scope names. A scope name is stored on the grant and means nothing outside the
 * table it was chosen for, so a list compiled in here would be wrong for one of them.
 */
export interface McpScope {
    name: string;
    label: string;
    tools: number;
    writes: number;
    default: boolean;
}

function isScope(value: unknown): value is McpScope {
    const scope = value as Partial<McpScope> | null;
    return (
        !!scope &&
        typeof scope.name === 'string' &&
        typeof scope.label === 'string' &&
        typeof scope.tools === 'number' &&
        typeof scope.writes === 'number'
    );
}

/**
 * The scopes a deployment offers, or none.
 *
 * ★ EVERY FAILURE MEANS "NO PICKER", AND THAT IS THE SAFE READING. A deployment on the
 * route-generated table answers with an empty list, and a worker that predates the endpoint answers
 * 404; both connect everything, exactly as this page did before it had a picker. Treating a failure
 * as an error instead would stop every connection to a deployment that simply has nothing to ask.
 * The one case this cannot tell apart is a Hubby deployment whose list failed to load — and there
 * the worker falls back to its own default set, not to everything.
 */
async function fetchScopes(worker: string): Promise<McpScope[]> {
    try {
        const response = await fetch(`${worker}/oauth/scopes`);
        if (!response.ok) return [];
        const body = (await response.json()) as { scopes?: unknown };
        return Array.isArray(body.scopes) ? body.scopes.filter(isScope) : [];
    } catch {
        return [];
    }
}

type Phase = 'checking' | 'consent' | 'working' | 'error';

export default function HubMcpConnectPage() {
    const { authorize, status, isAuthenticated, user, logout, profilePhoto, superUserName } =
        useSession();
    const [signingOut, setSigningOut] = useState(false);

    /**
     * Sign out and stay on this URL, so the login form comes up here and the consent screen follows
     * for whoever signs in next — the hand-off in the query string is untouched.
     *
     * Same sequence as the delivery shell's menu (`delivery-profile-menu.tsx`): `logout()` clears
     * the persisted session, then a full reload re-runs this page's own `authorize`, which now
     * finds nothing and renders `<Login />`. `reload()` rather than replacing the URL with itself,
     * which does not reload when it carries a hash.
     */
    const switchAccount = useCallback(() => {
        setSigningOut(true);
        logout();
        window.location.reload();
    }, [logout]);
    const [params] = useSearchParams();
    const handoff = params.get('handoff');
    /** The deployment that sent us here, once it has been checked against `MCP_ORIGINS`. */
    const worker = mcpOrigin(params);
    // ★ TEXT, NEVER MARKUP, AND NEVER TRUSTED. It comes from dynamic client registration, so
    // anyone can register a client called whatever they like — including something that reads as
    // if it came from us. React escapes it; the point is that nobody "improves" this later with
    // dangerouslySetInnerHTML.
    const clientName = params.get('client')?.slice(0, 80) || 'An AI assistant';

    const [phase, setPhase] = useState<Phase>('checking');
    const [error, setError] = useState<string>('');
    const authorizeRan = useRef(false);
    /** Null until the deployment has answered; empty when it offers no scopes. */
    const [scopes, setScopes] = useState<McpScope[] | null>(null);
    const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());

    useEffect(() => {
        if (!worker) return;
        let stale = false;
        void fetchScopes(worker).then(list => {
            if (stale) return;
            setScopes(list);
            setTicked(new Set(list.filter(scope => scope.default).map(scope => scope.name)));
        });
        return () => {
            stale = true;
        };
    }, [worker]);

    const toggle = useCallback((name: string) => {
        setTicked(current => {
            const next = new Set(current);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    }, []);

    const hasPicker = !!scopes && scopes.length > 0;
    /** Connect waits for the list, and for at least one tick when there is a list to tick. */
    const canApprove = scopes !== null && (!hasPicker || ticked.size > 0);

    useEffect(() => {
        if (authorizeRan.current) return;
        authorizeRan.current = true;
        void authorize();
    }, [authorize]);

    useEffect(() => {
        if (status === 'loading' || status === 'idle') return;
        setPhase(current => (current === 'checking' ? 'consent' : current));
    }, [status]);

    const mayConnect = useMemo(
        () => (user?.userType != null ? CONNECTABLE.includes(user.userType) : false),
        [user]
    );

    const approve = useCallback(async () => {
        if (!handoff || !worker || !user || !canApprove) return;
        setPhase('working');
        setError('');

        // ★ mode: 'user' IS THE DESIGN, not a fallback. The connection carries this user's own
        // permission context; a roles-mode token is brand-1 Admin only and would 403 anyway.
        const body: ApiTokenCreateBody = {
            note: `MCP: ${clientName}`.slice(0, 100),
            mode: 'user',
            // tblApiTokenType 2. Provenance only — it changes nothing about what the token may do.
            // What it buys is the label on the API & Webhooks page: this row appears without the
            // customer ever visiting that screen, and unlabelled it looks like one they forgot
            // creating, which is exactly the row they should not be guessing about.
            tokenType: 'mcpconnection',
            userId: Number(user.userId),
            active: true
        };

        const created = await Api.post<ApiTokenCreateBody, ApiTokenCreated>('apitokens', body);
        const apiToken = created.ok
            ? (created.data as ApiTokenCreated | undefined)?.value
            : undefined;
        if (!apiToken) {
            setError(copy.errors.tokenFailed);
            setPhase('error');
            return;
        }

        // Posted straight to the MCP worker rather than through our API: it is the worker's
        // hand-off, the token is single-purpose, and the id in the URL is the only thing tying
        // this browser to the pending connection.
        let response: Response;
        try {
            response = await fetch(`${worker}/oauth/handoff`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    handoff,
                    apiToken,
                    userId: String(user.userId),
                    userType: user.userType,
                    // In the deployment's own order, and omitted entirely when it offered none —
                    // an absent `scopes` is how the route-generated table reads "everything".
                    scopes: hasPicker
                        ? scopes.filter(scope => ticked.has(scope.name)).map(scope => scope.name)
                        : undefined
                })
            });
        } catch {
            setError(copy.errors.handoffFailed);
            setPhase('error');
            return;
        }

        if (response.status === 410) {
            setError(copy.errors.expired);
            setPhase('error');
            return;
        }
        if (!response.ok) {
            setError(copy.errors.handoffFailed);
            setPhase('error');
            return;
        }

        const { completeUrl } = (await response.json()) as { completeUrl: string };
        // A top-level navigation, not a fetch: the worker needs the cookie it set when this flow
        // started, which is how it knows this is the same browser.
        window.location.assign(completeUrl);
    }, [handoff, worker, user, clientName, canApprove, hasPicker, scopes, ticked]);

    if (!handoff || !worker) {
        return (
            <ConnectFrame>
                <Alert severity="info">{copy.errors.missingHandoff}</Alert>
            </ConnectFrame>
        );
    }

    if (phase === 'checking' || status === 'loading' || status === 'idle') {
        return (
            <ConnectFrame>
                <Stack alignItems="center" spacing={2}>
                    <CircularProgress size={28} />
                    <Typography variant="body2">{copy.checking}</Typography>
                </Stack>
            </ConnectFrame>
        );
    }

    // Not signed in: the ordinary login form, on this page. `stayOnPage` is what makes the customer
    // come straight back to the consent screen: without it the form navigates to the studio home
    // after signing in, and the connection request — which lives only in this URL — is lost. That
    // was this page's behaviour until 2026-10-03, despite a comment here saying otherwise; it showed
    // up the first time someone used "Not you? Sign out" and signed in as somebody else.
    if (!isAuthenticated) {
        return <Login stayOnPage />;
    }

    const account = user ? (
        <AccountCard
            name={[user.firstName, user.lastName].filter(Boolean).join(' ') || user.userName}
            login={user.userName}
            photo={profilePhoto}
            superUserName={superUserName}
            onSwitch={switchAccount}
            disabled={signingOut || phase === 'working'}
        />
    ) : null;

    if (!mayConnect) {
        // The account card here matters most: the likeliest way to land on this screen is being
        // signed in as the wrong one of two logins.
        return (
            <ConnectFrame>
                {account}
                <Typography variant="h6" gutterBottom>
                    {copy.notPermitted.heading}
                </Typography>
                <Typography variant="body1">{copy.notPermitted.body}</Typography>
            </ConnectFrame>
        );
    }

    return (
        <ConnectFrame>
            {account}
            <Typography variant="h5" gutterBottom>
                {copy.title}
            </Typography>
            <Typography variant="body1" gutterBottom>
                {copy.intro(clientName)}
            </Typography>

            <Typography variant="subtitle1" sx={{ mt: 3 }}>
                {copy.whatItCanDo.heading}
            </Typography>
            <Typography variant="body2">
                {hasPicker
                    ? copy.whatItCanDo.bodyScoped(user?.firstName ?? 'you')
                    : copy.whatItCanDo.body(user?.firstName ?? 'you')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
                {copy.whatItCanDo.narrower}
            </Typography>

            {scopes === null && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                    {copy.scopes.loading}
                </Typography>
            )}

            {hasPicker && (
                <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, mt: 3 }}>
                    <Typography component="legend" variant="subtitle1" sx={{ p: 0 }}>
                        {copy.scopes.heading}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {copy.scopes.help}
                    </Typography>
                    <FormGroup sx={{ mt: 1 }}>
                        {scopes.map(scope => (
                            <FormControlLabel
                                key={scope.name}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={ticked.has(scope.name)}
                                        onChange={() => toggle(scope.name)}
                                        disabled={phase === 'working'}
                                    />
                                }
                                label={
                                    <Typography variant="body2" component="span">
                                        {scope.label}
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            color="text.secondary"
                                        >
                                            {` — ${copy.scopes.detail(scope.tools, scope.writes)}`}
                                        </Typography>
                                    </Typography>
                                }
                            />
                        ))}
                    </FormGroup>
                    {ticked.size === 0 && (
                        <Typography variant="body2" color="error" role="alert">
                            {copy.scopes.none}
                        </Typography>
                    )}
                </Box>
            )}

            <Typography variant="subtitle1" sx={{ mt: 3 }}>
                {copy.control.heading}
            </Typography>
            <Box component="ul" sx={{ pl: 3, my: 1 }}>
                {copy.control.items.map(item => (
                    <Typography component="li" variant="body2" key={item}>
                        {item}
                    </Typography>
                ))}
            </Box>

            <Alert severity="warning" sx={{ mt: 3 }}>
                {copy.beforeYouApprove}
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={() => void approve()}
                    disabled={phase === 'working' || !canApprove || signingOut}
                >
                    {copy.approve}
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => window.location.assign(`${worker}/oauth/deny`)}
                    disabled={phase === 'working'}
                >
                    {copy.deny}
                </Button>
            </Stack>
        </ConnectFrame>
    );
}

/**
 * ★ THE FRAME IS THE SCROLLPORT, BECAUSE NOTHING ABOVE IT SCROLLS. The hub shell sets
 * `overflow: hidden` on `html` and `body` (`App.css`) so its full-viewport layouts can own their
 * scrolling, and every other hub page fits in a screen. This one stopped fitting when the scope
 * checklist arrived: sixteen rows pushed Connect below the fold with no way to reach it, and the
 * first real connection through it was approved by editing the page in dev tools (2026-10-02).
 *
 * So the outer box is exactly one viewport tall and scrolls, and the inner one centres the content
 * with `min-height: 100%` rather than the outer centring it — flex-centring something taller than
 * its scroll container cuts the top off where no scrolling can reach it.
 */
/**
 * Who the connection will run as, with the way out if that is the wrong account.
 */
function AccountCard({
    name,
    login,
    photo,
    superUserName,
    onSwitch,
    disabled
}: {
    name: string;
    /** The sign-in name, which is what tells two logins with one display name apart. */
    login?: string;
    photo: React.ComponentProps<typeof Media>['media'];
    superUserName?: string;
    onSwitch: () => void;
    disabled: boolean;
}) {
    return (
        <Box
            data-testid="mcp-connect-account"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                mb: 3,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1
            }}
        >
            <Media
                media={photo}
                options={{ width: 96 }}
                alt={name}
                sx={{ width: 44, height: 44 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                    {copy.account.signedInAs}
                </Typography>
                <Typography variant="subtitle2" component="div" noWrap>
                    {name}
                </Typography>
                {login && login !== name && (
                    <Typography variant="body2" color="text.secondary" component="div" noWrap>
                        {login}
                    </Typography>
                )}
                {superUserName && (
                    <Typography variant="caption" color="text.secondary" component="div">
                        {copy.account.via(superUserName)}
                    </Typography>
                )}
            </Box>
            <Button
                size="small"
                onClick={onSwitch}
                disabled={disabled}
                title={copy.account.switchHelp}
                sx={{ flexShrink: 0 }}
            >
                {copy.account.switch}
            </Button>
        </Box>
    );
}

function ConnectFrame({ children }: { children: React.ReactNode }) {
    return (
        <Box data-testid="mcp-connect-frame" sx={{ height: '100dvh', overflowY: 'auto' }}>
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                // More room top and bottom than at the sides (Herb, 2026-10-02): the content is a
                // narrow column, and on a tall screen it otherwise sat tight against both edges.
                sx={{ minHeight: '100%', px: 2, py: { xs: 4, sm: 6 }, boxSizing: 'border-box' }}
            >
                <Box sx={{ maxWidth: 560, width: '100%' }}>{children}</Box>
            </Box>
        </Box>
    );
}
